import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown } from 'lucide-react';

const lines = [
  { text: 'Clarity in', italic: false },
  { text: 'every number.', italic: true },
];

const ease = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen bg-navy overflow-hidden flex items-center">
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #C5A059 0%, transparent 45%)' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full pt-32 pb-24 grid lg:grid-cols-12 gap-16 items-center relative z-10">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            data-testid="hero-eyebrow"
            className="text-xs tracking-[0.35em] uppercase text-gold mb-10"
          >
            <span className="eyebrow-rule" />
            Chapel Hill, North Carolina
          </motion.p>

          <h1 data-testid="hero-heading" className="font-serif text-cream tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-2">
                <motion.span
                  className={`block ${line.italic ? 'italic text-gold-light' : ''}`}
                  initial={{ y: '110%' }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.45 + i * 0.15, ease }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease }}
            data-testid="hero-subcopy"
            className="mt-8 max-w-md text-cream/70 font-light leading-relaxed text-base"
          >
            For more than <span className="font-semibold text-lg text-cream">60</span> years, A. G. Mangum &amp; Co. has been a trusted advisor to individuals, business owners, executives, and health care professionals across the Triangle — building lasting relationships through careful, personal tax and financial counsel.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            <a
              href="tel:+19199675733"
              data-testid="hero-call-button"
              className="group bg-gold text-navy text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-gold-light"
            >
              Call 919-967-5733
            </a>
            <a
              href="#services"
              data-testid="hero-explore-link"
              onClick={(e) => { e.preventDefault(); document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="text-xs tracking-[0.2em] uppercase text-cream/70 border-b border-gold/50 pb-1 transition-colors duration-300 hover:text-gold"
            >
              Explore our services
            </a>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.7, ease }}
            className="relative"
          >
            <div className="absolute -top-5 -left-5 w-full h-full border border-gold/40" aria-hidden="true" />
            <div className="relative overflow-hidden aspect-[3/4] bg-gold">
              <motion.div style={{ y: imgY }} className="absolute -top-[7%] left-0 w-full h-[114%]">
                <div className="absolute inset-0" style={{ clipPath: 'polygon(0 0, 62% 0, 34% 100%, 0 100%)' }}>
                  <img
                    src="https://images.pexels.com/photos/32907649/pexels-photo-32907649.jpeg"
                    alt="UNC Chapel Hill campus"
                    data-testid="hero-image"
                    className="absolute max-w-none"
                    style={{ width: '200%', height: 'auto', left: '-69%', top: '-60%' }}
                  />
                </div>
                <div className="absolute inset-0" style={{ clipPath: 'polygon(63.5% 0, 100% 0, 100% 100%, 35.5% 100%)' }}>
                  <img
                    src="https://images.unsplash.com/photo-1760016445152-a7c46429424d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxsdWNreSUyMHN0cmlrZSUyMHdhdGVyJTIwdG93ZXIlMjBkdXJoYW18ZW58MHx8fHwxNzkxMjA4ODQwfDA&ixlib=rb-4.1.0&q=85"
                    alt="Lucky Strike water tower, Durham"
                    data-testid="hero-image-durham"
                    className="absolute max-w-none"
                    style={{ width: '225%', height: 'auto', left: '35%', top: '-83%' }}
                  />
                </div>
              </motion.div>
              <div className="absolute inset-0 bg-navy/25" />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 1 }}
              className="absolute -bottom-8 -left-12 bg-cream text-navy text-[10px] tracking-[0.3em] uppercase px-6 py-4 shadow-[0_8px_30px_rgb(0,0,0,0.12)]"
            >
              Serving the Triangle since 1966
            </motion.p>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-cream/40"
      >
        <ArrowDown size={18} className="animate-bounce" aria-hidden="true" />
      </motion.div>
    </section>
  );
}
